import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FolderPlus, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { ALBUM_KIND, manifestTags, type AlbumManifest } from '@/lib/album';
import { generateAlbumId } from '@/lib/albumCrypto';

interface AddSubAlbumDialogProps {
  parent: AlbumManifest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Creates a sub-album: a fresh manifest event whose contents are encrypted with
 * a key derived from the same master key + salt, but its own d-tag — so one
 * link unlocks the whole tree.
 */
export function AddSubAlbumDialog({ parent, open, onOpenChange }: AddSubAlbumDialogProps) {
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    const subName = name.trim() || 'Sub-album';
    try {
      const subD = generateAlbumId();
      // Sub-album event carries the same public salt; its own d-tag makes its key unique.
      await publish({
        kind: ALBUM_KIND,
        content: '',
        tags: manifestTags({ d: subD, title: subName, salt: parent.salt, parent: parent.d, encryptedKey: parent.encryptedKey, files: [], subs: [] }),
      });
      // Republish the parent with the new sub reference.
      await publish({
        kind: ALBUM_KIND,
        content: '',
        tags: manifestTags({
          d: parent.d,
          title: parent.title,
          salt: parent.salt,
          cover: parent.cover,
          encryptedKey: parent.encryptedKey,
          subs: [...parent.subs, { d: subD, name: subName }],
          files: parent.files,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['album'] });
      queryClient.invalidateQueries({ queryKey: ['my-albums'] });
      onOpenChange(false);
      setName('');
    } catch (e) {
      console.error(e);
      toast({ title: 'Could not create sub-album', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="size-4" /> New sub-album
          </DialogTitle>
          <DialogDescription>
            Sub-albums get their own derived encryption key, but are unlocked by the same album link.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sub-name">Sub-album name</Label>
            <Input
              id="sub-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ceremony, Party, Behind the scenes"
              maxLength={80}
            />
          </div>
          <Button className="w-full" onClick={create} disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <FolderPlus className="size-4" />}
            Create sub-album
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
